/**
 * Desglose detallado para reservas por horas en ListingPage (OrderPanel).
 * Líneas de precio, total, ahorro y nota de comisión (sin fechas ni grupo en este bloque).
 */
import React from 'react';
import { object, string } from 'prop-types';
import classNames from 'classnames';
import Decimal from 'decimal.js';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import {
  formatMoney,
  convertMoneyToNumber,
  convertUnitToSubUnit,
  unitDivisor,
} from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_DAY,
  LINE_ITEM_FIRST_BOOKING_WELCOME_DISCOUNT,
  LINE_ITEM_WEEKDAY_BOOKING_DISCOUNT,
  LINE_ITEM_HOUR,
  LINE_ITEM_ITEM,
  LINE_ITEM_NIGHT,
  LINE_ITEM_PICKUP_FEE,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_SHIPPING_FEE,
  LINE_ITEM_POOL_USE_EXTRA,
  propTypes,
} from '../../util/types';
import { indoorSpaceTitleForLineItem, isIndoorSpaceLineItem } from '../../util/indoorSpaces';

import css from './ListingReservationBreakdown.module.css';

const { Money } = sdkTypes;

const estimatedCustomerPayinMoney = (lineItems, marketplaceCurrency) => {
  const customerItems = lineItems.filter(li => li.includeFor && li.includeFor.includes('customer'));
  if (!customerItems.length) {
    return null;
  }
  const numericTotalPrice = customerItems.reduce((sum, lineItem) => {
    const numericPrice = convertMoneyToNumber(lineItem.lineTotal);
    return new Decimal(numericPrice).add(sum);
  }, new Decimal(0));
  const currency = customerItems[0].lineTotal?.currency || marketplaceCurrency;
  return new Money(
    convertUnitToSubUnit(numericTotalPrice.toNumber(), unitDivisor(currency)),
    currency
  );
};

/** Extras de listing: `line-item/<nombre>` definidos en lineItems.js (no códigos fijos). */
const KNOWN_LINE_ITEM_CODES = new Set([
  LINE_ITEM_HOUR,
  LINE_ITEM_DAY,
  LINE_ITEM_NIGHT,
  LINE_ITEM_ITEM,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_FIRST_BOOKING_WELCOME_DISCOUNT,
  LINE_ITEM_WEEKDAY_BOOKING_DISCOUNT,
  LINE_ITEM_SHIPPING_FEE,
  LINE_ITEM_PICKUP_FEE,
]);

const isListingExtraLineItem = li => {
  if (!li || li.reversal) return false;
  if (!li.includeFor || !li.includeFor.includes('customer')) return false;
  if (!li.code || !li.code.startsWith('line-item/')) return false;
  if (KNOWN_LINE_ITEM_CODES.has(li.code)) return false;
  if (li.code.startsWith('line-item/guest-count')) return false;
  return true;
};

const extraLabel = (code, listing) => {
  if (code === LINE_ITEM_POOL_USE_EXTRA) {
    return <FormattedMessage id="OrderBreakdown.poolUseExtra" />;
  }
  if (isIndoorSpaceLineItem(code)) {
    const title = indoorSpaceTitleForLineItem(listing, code);
    return title ? (
      <FormattedMessage id="OrderBreakdown.indoorSpaceExtraNamed" values={{ title }} />
    ) : (
      <FormattedMessage id="OrderBreakdown.indoorSpaceExtra" />
    );
  }
  if (!code || !code.startsWith('line-item/')) return '';
  return code.slice('line-item/'.length);
};

const sumLineTotalsForItems = (lineItemsSubset, marketplaceCurrency) => {
  const list = (lineItemsSubset || []).filter(Boolean);
  if (!list.length) return null;
  const numericTotalPrice = list.reduce((sum, lineItem) => {
    if (!lineItem.lineTotal) return sum;
    const numericPrice = convertMoneyToNumber(lineItem.lineTotal);
    return new Decimal(numericPrice).add(sum);
  }, new Decimal(0));
  const cur = list[0].lineTotal?.currency || marketplaceCurrency;
  return new Money(convertUnitToSubUnit(numericTotalPrice.toNumber(), unitDivisor(cur)), cur);
};

const PriceRow = ({ label, value, valueClassName, labelClassName }) => (
  <div className={css.priceRow}>
    <span className={classNames(css.rowLabel, labelClassName)}>{label}</span>
    <span className={classNames(css.rowValue, valueClassName)}>{value}</span>
  </div>
);

const numericPercentage = raw =>
  raw != null ? Number(typeof raw.valueOf === 'function' ? raw.valueOf() : raw) : null;

const ListingReservationBreakdownComponent = props => {
  const { lineItems, listing, currency, marketplaceName, intl } = props;

  const hourLine = lineItems.find(li => li.code === LINE_ITEM_HOUR && !li.reversal);
  const weekdayDiscountLine = lineItems.find(
    li => li.code === LINE_ITEM_WEEKDAY_BOOKING_DISCOUNT && !li.reversal
  );
  const discountLine = lineItems.find(
    li => li.code === LINE_ITEM_FIRST_BOOKING_WELCOME_DISCOUNT && !li.reversal
  );
  const commissionLine = lineItems.find(
    li => li.code === LINE_ITEM_CUSTOMER_COMMISSION && !li.reversal
  );

  // The guest never sees the commission as its own line here (unlike the
  // checkout OrderBreakdown) — every price shown already has it baked in. So
  // instead of showing the server's raw (pre-commission) discount amounts, we
  // mark up the undiscounted base by the commission rate first, then apply
  // each discount percentage to that gross figure, in the same order the
  // server applies them (weekday, then first-booking). The commission is a
  // straight percentage, so discounting-then-marking-up and
  // marking-up-then-discounting land on the exact same real total
  // (multiplication commutes) — only the displayed intermediate rows change,
  // and they now reconcile with the row shown directly above them. The
  // authoritative "Precio total" below is untouched: it still comes straight
  // from the server's own line items.
  const commissionPct = commissionLine ? numericPercentage(commissionLine.percentage) || 0 : 0;
  const grossAmount = amount => Math.round(amount * (1 + commissionPct / 100));
  const percentOf = (amount, pct) => Math.round(amount * (pct / 100));

  const effectiveHourLineTotal = hourLine
    ? new Money(grossAmount(hourLine.lineTotal.amount), hourLine.lineTotal.currency)
    : null;
  const effectiveHourUnitPrice =
    hourLine && effectiveHourLineTotal && Number(hourLine.quantity) > 0
      ? new Money(
          Math.round(effectiveHourLineTotal.amount / Number(hourLine.quantity)),
          effectiveHourLineTotal.currency
        )
      : null;
  const unitPriceFormatted = effectiveHourUnitPrice
    ? formatMoney(intl, effectiveHourUnitPrice)
    : null;
  const quantity = hourLine ? hourLine.quantity : null;
  const baseTotalFormatted = effectiveHourLineTotal
    ? formatMoney(intl, effectiveHourLineTotal)
    : null;

  const extraLineItems = lineItems
    .filter(isListingExtraLineItem)
    .map(li =>
      li.lineTotal
        ? { ...li, lineTotal: new Money(grossAmount(li.lineTotal.amount), li.lineTotal.currency) }
        : li
    );
  const subtotalMoney =
    extraLineItems.length > 0 && effectiveHourLineTotal
      ? sumLineTotalsForItems([{ lineTotal: effectiveHourLineTotal }, ...extraLineItems], currency)
      : null;
  const subtotalFormatted = subtotalMoney ? formatMoney(intl, subtotalMoney) : null;

  const grossReferenceAmount = (subtotalMoney || effectiveHourLineTotal)?.amount ?? 0;

  const weekdayPct = weekdayDiscountLine
    ? Math.abs(numericPercentage(weekdayDiscountLine.percentage))
    : null;
  const weekdayDiscountAmount =
    weekdayDiscountLine && Number.isFinite(weekdayPct)
      ? percentOf(grossReferenceAmount, weekdayPct)
      : 0;
  const weekdayDiscountFormatted = weekdayDiscountLine
    ? formatMoney(intl, new Money(-weekdayDiscountAmount, currency))
    : null;

  const afterWeekdayAmount = grossReferenceAmount - weekdayDiscountAmount;

  const firstBookingPct = discountLine
    ? Math.abs(numericPercentage(discountLine.percentage))
    : null;
  const firstBookingDiscountAmount =
    discountLine && Number.isFinite(firstBookingPct)
      ? percentOf(afterWeekdayAmount, firstBookingPct)
      : 0;
  const discountFormatted = discountLine
    ? formatMoney(intl, new Money(-firstBookingDiscountAmount, currency))
    : null;
  const savingsAmount = discountLine ? new Money(firstBookingDiscountAmount, currency) : null;
  const savingsFormatted = savingsAmount ? formatMoney(intl, savingsAmount) : null;

  const totalMoney = estimatedCustomerPayinMoney(lineItems, currency);
  const totalFormatted = totalMoney ? formatMoney(intl, totalMoney) : null;

  const baseLineLabel =
    unitPriceFormatted && quantity != null ? (
      <FormattedMessage
        id="OrderBreakdown.baseUnitHour"
        values={{
          unitPrice: unitPriceFormatted,
          quantity,
        }}
      />
    ) : null;

  return (
    <div className={css.root}>
      {baseLineLabel && baseTotalFormatted ? (
        <PriceRow label={baseLineLabel} value={baseTotalFormatted} />
      ) : null}

      {extraLineItems.map(li => (
        <PriceRow
          key={li.code}
          label={extraLabel(li.code, listing)}
          value={formatMoney(intl, li.lineTotal)}
        />
      ))}

      {subtotalFormatted ? (
        <PriceRow
          label={<FormattedMessage id="OrderBreakdown.subTotal" />}
          value={subtotalFormatted}
          valueClassName={css.subtotalValue}
        />
      ) : null}

      {weekdayDiscountLine &&
      weekdayDiscountFormatted &&
      weekdayPct != null &&
      Number.isFinite(weekdayPct) ? (
        <PriceRow
          label={
            <FormattedMessage
              id="ListingReservationBreakdown.weekdayDiscountLabel"
              values={{ percent: weekdayPct }}
            />
          }
          value={weekdayDiscountFormatted}
          valueClassName={css.discountValue}
        />
      ) : null}

      {discountLine && discountFormatted ? (
        <PriceRow
          label={<FormattedMessage id="ListingReservationBreakdown.welcomeDiscountLabel" />}
          value={discountFormatted}
          valueClassName={css.discountValue}
        />
      ) : null}

      {totalFormatted ? (
        <div className={css.totalBlock}>
          <div className={classNames(css.priceRow, css.totalRow)}>
            <span className={css.totalLabel}>
              <FormattedMessage id="ListingReservationBreakdown.totalCostLabel" />
            </span>
            <span className={classNames(css.rowValue, css.totalValue)}>{totalFormatted}</span>
          </div>
        </div>
      ) : null}

      {savingsFormatted ? (
        <div className={css.savingsNote} role="status">
          <span className={css.savingsIcon} aria-hidden="true">
            ✅
          </span>
          <div className={css.savingsNoteText}>
            <FormattedMessage
              id="ListingReservationBreakdown.youSave"
              values={{
                amount: <span className={css.savingsAmountBold}>{savingsFormatted}</span>,
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

ListingReservationBreakdownComponent.defaultProps = {
  listing: null,
};

ListingReservationBreakdownComponent.propTypes = {
  lineItems: propTypes.lineItems.isRequired,
  listing: object,
  currency: string.isRequired,
  marketplaceName: string.isRequired,
  intl: intlShape.isRequired,
};

const ListingReservationBreakdown = injectIntl(ListingReservationBreakdownComponent);
ListingReservationBreakdown.displayName = 'ListingReservationBreakdown';

export default ListingReservationBreakdown;
